import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  LIBELLE_PALIER_BOITIER, LIBELLE_STATUT, ageSignalMs, classeFraicheur, classeStatut, compterParStatut, decrireJournalBoitier,
  dernierSignalDe, etatPassagePlanifie, filtrerBoitiers, libelleAge, libelleAuteurBoitier, libelleEligibilite, messageBoitier,
  phraseSynchroTraccar, validerImei, validerNoteBoitier,
  type BoitierInconnu, type EntreeJournalBoitier, type FiltreBoitiers, type LigneBoitier, type OrgPourBoitier,
  type ResumeAlertesOrg, type SanteAlertes,
} from '../api/boitiers'
import { dateHeure } from '../api/format'
import type { Source } from '../api/types'
import { Badge } from './Badges'
import { Icone } from './Icone'
import { Modale } from './Modale'

/** Les agrégats d'alertes : `absente` = migration 0052 pas encore poussée (section grisée), `indisponible` = lecture en erreur. */
type EtatAlertes =
  | { statut: 'absente' }
  | { statut: 'indisponible' }
  | { statut: 'prete'; lignes: ResumeAlertesOrg[]; sante: SanteAlertes | null }

type Etat =
  | { statut: 'chargement' }
  | { statut: 'erreur'; message: string }
  | {
      statut: 'pret'
      boitiers: LigneBoitier[]
      inconnus: BoitierInconnu[]
      orgs: OrgPourBoitier[]
      journal: EntreeJournalBoitier[]
      alertes: EtatAlertes
      /** Ce qui n'a pas pu être lu sans empêcher le reste (menu d'attribution, journal, inconnus). */
      avertissements: string[]
    }

const FILTRES: { code: FiltreBoitiers; libelle: string }[] = [
  { code: 'tous', libelle: 'Tous' },
  { code: 'attribue', libelle: 'Attribués' },
  { code: 'stock', libelle: 'En stock' },
  { code: 'desactive', libelle: 'Désactivés' },
]

/** Une action en cours sur un boîtier : on attribue, on retire ou on désactive (chacune demande une confirmation). */
type Action =
  | { type: 'ajout' }
  | { type: 'attribuer'; imei: string }
  | { type: 'retirer'; boitier: LigneBoitier }
  | { type: 'desactiver'; boitier: LigneBoitier }

/**
 * Écran « Boîtiers GPS » (migration 0050) : un seul Traccar partagé, l'IMEI désigne le propriétaire. La supervision
 * attribue chaque boîtier à un client (tous les paliers, Starter compris, depuis la 0054) ; un client ne peut rien enregistrer
 * lui-même. Les boîtiers qui parlent sans être attribués sont comptés par le webhook et proposés ici « à ajouter au stock ».
 */
export function BoitiersGps({ source, notifier }: { source: Source; notifier: (m: string) => void }) {
  const [etat, setEtat] = useState<Etat>({ statut: 'chargement' })
  const [action, setAction] = useState<Action | null>(null)
  const [filtre, setFiltre] = useState<FiltreBoitiers>('tous')
  const [recherche, setRecherche] = useState('')
  const [maintenant, setMaintenant] = useState(() => Date.now())
  const [ficheJournal, setFicheJournal] = useState<string | null>(null)
  /** IMEI en cours de synchronisation avec Traccar (un seul à la fois). */
  const [synchro, setSynchro] = useState<string | null>(null)

  /** `silencieux` : garde l'écran en place pendant le rechargement (après une écriture ou une actualisation). Ne lève jamais. */
  const charger = useCallback(async (silencieux: boolean) => {
    if (!silencieux) setEtat({ statut: 'chargement' })
    try {
      const boitiers = await source.boitiers()
      const avertissements: string[] = []
      const lire = async <T,>(nom: string, lecture: () => Promise<T[]>): Promise<T[]> => {
        try { return await lecture() } catch (e) { avertissements.push(`${nom} : ${messageBoitier(e)}`); return [] }
      }
      const [inconnus, orgs, journal, alertes] = await Promise.all([
        lire('Boîtiers inconnus', () => source.boitiersInconnus()),
        lire("Menu d'attribution", () => source.orgsPourBoitiers()),
        lire('Journal', () => source.journalBoitiers(null)),
        lireAlertes(source, (m) => avertissements.push(m)),
      ])
      setEtat({ statut: 'pret', boitiers, inconnus, orgs, journal: journal.slice(0, 50), alertes, avertissements })
      setMaintenant(Date.now())
    } catch (e) {
      const message = messageBoitier(e)
      if (silencieux) notifier(`Rechargement impossible : ${message}`)
      else setEtat({ statut: 'erreur', message })
    }
  }, [source, notifier])

  useEffect(() => { void charger(false) }, [charger])
  // L'âge d'un signal se rafraîchit seul ; les données, elles, à la demande (bouton « Actualiser »).
  useEffect(() => {
    const t = setInterval(() => setMaintenant(Date.now()), 30_000)
    return () => clearInterval(t)
  }, [])

  const pret = etat.statut === 'pret' ? etat : null
  const compte = useMemo(() => compterParStatut(pret?.boitiers ?? []), [pret])
  const affiches = useMemo(() => filtrerBoitiers(pret?.boitiers ?? [], filtre, recherche), [pret, filtre, recherche])
  const silencieux = useMemo(
    () => (pret?.boitiers ?? []).filter((b) => b.statut === 'attribue' && (ageSignalMs(dernierSignalDe(b), maintenant) ?? Infinity) > 24 * 3_600_000).length,
    [pret, maintenant],
  )

  async function ajouterAuStock(imei: string) {
    try {
      await source.ajouterBoitierAuStock(imei, null)
      notifier(`Boîtier ${imei} ajouté au stock`)
      await charger(true)
    } catch (e) {
      notifier(messageBoitier(e))
    }
  }

  async function synchroniser(b: LigneBoitier) {
    if (synchro) return
    setSynchro(b.imei)
    try {
      notifier(phraseSynchroTraccar(await source.synchroniserTraccar(b.imei)))
    } catch (e) {
      notifier(e instanceof Error && e.message ? e.message : messageBoitier(e))
    } finally {
      setSynchro(null)
    }
  }

  async function reactiver(b: LigneBoitier) {
    try {
      await source.definirBoitierActif(b.imei, true, null)
      notifier(`Boîtier ${b.imei} réactivé`)
      await charger(true)
    } catch (e) {
      notifier(messageBoitier(e))
    }
  }

  return (
    <>
      <div className="tete-page">
        <h1>Boîtiers GPS</h1>
        <p>Un seul serveur Traccar pour tous les clients : chaque boîtier (IMEI) est attribué ici à son propriétaire</p>
      </div>

      {etat.statut === 'chargement' && (
        <div className="kpis" aria-busy="true" aria-label="Chargement des boîtiers">
          {Array.from({ length: 4 }, (_, i) => (
            <div className="carte kpi" key={i}><div className="sk" /><div className="sk court" /></div>
          ))}
        </div>
      )}

      {etat.statut === 'erreur' && (
        <div className="carte vide" role="alert">
          <Icone nom="alert" taille={22} />
          <p>Chargement impossible : {etat.message}</p>
          <button type="button" className="btn btn-plein btn-sm" onClick={() => void charger(false)}>
            <Icone nom="refresh" /> Réessayer
          </button>
        </div>
      )}

      {pret && (
        <>
          {pret.avertissements.map((a) => (
            <p className="encart encart-alerte" role="status" key={a}><Icone nom="alert" /> {a}</p>
          ))}

          <div className="kpis">
            <Compteur libelle="Attribués" valeur={compte.attribue} detail="à un client ou à l'opératrice" />
            <Compteur libelle="En stock" valeur={compte.stock} detail="prêts à attribuer" />
            <Compteur libelle="Désactivés" valeur={compte.desactive} detail="positions ignorées" />
            <Compteur
              libelle="Inconnus qui parlent" valeur={pret.inconnus.length}
              detail={pret.inconnus.length > 0 ? 'à ajouter au stock' : 'aucun'} alerte={pret.inconnus.length > 0}
            />
            <Compteur libelle="Attribués muets (24 h)" valeur={silencieux} detail="boîtier éteint ou sans réseau ?" alerte={silencieux > 0} />
          </div>

          {pret.inconnus.length > 0 && (
            <section className="carte" aria-labelledby="titre-inconnus">
              <div className="carte-tete">
                <div>
                  <h2 id="titre-inconnus">Boîtiers inconnus qui parlent</h2>
                  <p className="sous">
                    Ils sont déclarés dans Traccar mais absents du registre : leurs positions sont ignorées (rien n'est enregistré).
                  </p>
                </div>
              </div>
              <div className="defile">
                <table>
                  <thead>
                    <tr>
                      <th>IMEI</th><th className="c">Messages reçus</th><th>Premier signal</th><th>Dernier signal</th>
                      <th><span className="sr">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pret.inconnus.map((i) => (
                      <tr key={i.imei}>
                        <td className="fort imei">{i.imei}</td>
                        <td className="c n">{i.nb_messages}</td>
                        <td className="discret-t">{dateHeure(i.premier_signal)}</td>
                        <td className="discret-t">{libelleAge(ageSignalMs(i.dernier_signal, maintenant))}</td>
                        <td>
                          <div className="actions">
                            <button type="button" className="btn btn-plein btn-sm" onClick={() => void ajouterAuStock(i.imei)} aria-label={`Ajouter ${i.imei} au stock`}>
                              <Icone nom="plus" /> Ajouter au stock
                            </button>
                            <button type="button" className="btn btn-doux btn-sm" onClick={() => setAction({ type: 'attribuer', imei: i.imei })} aria-label={`Attribuer ${i.imei} à un client`}>
                              Attribuer…
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section className="carte" aria-labelledby="titre-boitiers">
            <div className="carte-tete">
              <div>
                <h2 id="titre-boitiers">Tous les boîtiers</h2>
                <p className="sous">{compte.tous} boîtier{compte.tous > 1 ? 's' : ''} au registre de la plateforme</p>
              </div>
              <div className="actions">
                <button type="button" className="btn btn-doux btn-sm" onClick={() => void charger(true)}>
                  <Icone nom="refresh" /> Actualiser
                </button>
                <button type="button" className="btn btn-plein btn-sm" onClick={() => setAction({ type: 'ajout' })}>
                  <Icone nom="plus" /> Ajouter un boîtier
                </button>
              </div>
            </div>

            <div className="barre-filtres">
              <div className="raccourcis" role="group" aria-label="Filtrer par statut">
                {FILTRES.map((f) => (
                  <button
                    key={f.code} type="button" aria-pressed={filtre === f.code}
                    className={`puce-bouton${filtre === f.code ? ' on' : ' discrete'}`}
                    onClick={() => setFiltre(f.code)}
                  >
                    {f.libelle} · {compte[f.code]}
                  </button>
                ))}
              </div>
              <div className="champ recherche">
                <label className="sr" htmlFor="recherche-boitiers">Rechercher un boîtier</label>
                <input
                  id="recherche-boitiers" type="search" value={recherche} placeholder="IMEI, client, nom ou note"
                  onChange={(e) => setRecherche(e.target.value)} autoComplete="off"
                />
              </div>
            </div>

            <div className="defile">
              <table>
                <thead>
                  <tr>
                    <th>Boîtier</th><th>Statut</th><th>Client</th><th>Dernier signal</th><th><span className="sr">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {affiches.length === 0 ? (
                    <tr><td colSpan={5}>
                      <div className="vide">
                        {compte.tous === 0
                          ? "Aucun boîtier au registre. Ajoutez le premier avec « Ajouter un boîtier », ou attendez qu'un boîtier déclaré dans Traccar parle."
                          : 'Aucun boîtier ne correspond à ce filtre.'}
                      </div>
                    </td></tr>
                  ) : affiches.map((b) => (
                    <LigneTableau
                      key={b.imei} b={b} maintenant={maintenant}
                      surAttribuer={() => setAction({ type: 'attribuer', imei: b.imei })}
                      surRetirer={() => setAction({ type: 'retirer', boitier: b })}
                      surDesactiver={() => setAction({ type: 'desactiver', boitier: b })}
                      surReactiver={() => void reactiver(b)}
                      surSynchroniser={() => void synchroniser(b)}
                      synchroEnCours={synchro === b.imei}
                      synchroBloquee={synchro !== null}
                      surJournal={() => setFicheJournal(b.imei)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <p className="note">
              Un boîtier ne parle à la plateforme que s'il est <b>attribué</b> à un client dont le palier inclut l'option (Pro ou Business) ; l'opératrice en est
              exemptée. Un client rétrogradé n'est plus alimenté (« option indisponible »). Les clients ne peuvent enregistrer aucun IMEI eux-mêmes.
            </p>
          </section>

          <AlertesClients etat={pret.alertes} maintenant={maintenant} />

          <section className="carte" aria-labelledby="titre-journal">
            <div className="carte-tete">
              <div>
                <h2 id="titre-journal">Journal</h2>
                <p className="sous">Toutes les attributions, retraits et désactivations — qui, quand, pourquoi (50 derniers)</p>
              </div>
            </div>
            <div className="carte-corps">
              <JournalBoitiers lignes={pret.journal} />
            </div>
          </section>
        </>
      )}

      {pret && action?.type === 'ajout' && (
        <ModaleAjout
          source={source} surFermer={() => setAction(null)}
          surFait={async (imei) => { notifier(`Boîtier ${imei} ajouté au stock`); await charger(true) }}
        />
      )}
      {pret && action?.type === 'attribuer' && (
        <ModaleAttribution
          imei={action.imei} orgs={pret.orgs} source={source} surFermer={() => setAction(null)}
          surFait={async (nom) => { notifier(`Boîtier ${action.imei} attribué à ${nom}`); await charger(true) }}
        />
      )}
      {pret && action?.type === 'retirer' && (
        <ModaleConfirmation
          titre="Retirer le boîtier" sousTitre={action.boitier.imei} libelle="Retirer le boîtier" danger
          avertissement={`Le boîtier retourne en stock : « ${action.boitier.org_nom ?? 'son client'} » ne reçoit plus ses positions (sa fiche est désactivée, son historique reste visible chez lui).`}
          confirmer={(note) => source.retirerBoitier(action.boitier.imei, note)}
          surFermer={() => setAction(null)}
          surFait={async () => { notifier(`Boîtier ${action.boitier.imei} retiré, de retour en stock`); await charger(true) }}
        />
      )}
      {pret && action?.type === 'desactiver' && (
        <ModaleConfirmation
          titre="Désactiver le boîtier" sousTitre={action.boitier.imei} libelle="Désactiver" danger
          avertissement="Ses positions seront ignorées (boîtier volé, litige…). Le propriétaire reste inscrit ; on peut le réactiver à tout moment."
          confirmer={(note) => source.definirBoitierActif(action.boitier.imei, false, note)}
          surFermer={() => setAction(null)}
          surFait={async () => { notifier(`Boîtier ${action.boitier.imei} désactivé`); await charger(true) }}
        />
      )}
      {ficheJournal && <ModaleJournal imei={ficheJournal} source={source} surFermer={() => setFicheJournal(null)} />}
    </>
  )
}

/** Lit les agrégats d'alertes ; migration absente = section grisée, autre erreur = avertissement et section grisée aussi. */
async function lireAlertes(source: Source, avertir: (m: string) => void): Promise<EtatAlertes> {
  try {
    const [lignes, sante] = await Promise.all([source.resumeAlertesGps(), source.santeAlertesGps()])
    if (lignes === null) return { statut: 'absente' }
    return { statut: 'prete', lignes, sante }
  } catch (e) {
    avertir(`Alertes GPS des clients : ${messageBoitier(e)}`)
    return { statut: 'indisponible' }
  }
}

/** « Alertes GPS des clients » : compteurs par organisation et santé du minuteur. Jamais le contenu d'une alerte ni une zone. */
function AlertesClients({ etat, maintenant }: { etat: EtatAlertes; maintenant: number }) {
  const passage = etat.statut === 'prete' ? etatPassagePlanifie(etat.sante?.dernier_passage ?? null, maintenant) : null
  return (
    <section className="carte" aria-labelledby="titre-alertes">
      <div className="carte-tete">
        <div>
          <h2 id="titre-alertes">Alertes GPS des clients</h2>
          <p className="sous">Règles, zones, alertes et commandes par organisation — des compteurs, rien d'autre</p>
        </div>
        {passage && (
          <div title="Le minuteur des alertes passe chaque minute">
            <Badge classe={passage.ok ? 'vert' : 'rouge'}>{`Passage planifié : ${passage.libelle}`}</Badge>
          </div>
        )}
      </div>

      {etat.statut === 'absente' && (
        <div className="carte-corps">
          <p className="vide" role="status">
            Les alertes GPS ne sont pas encore installées sur la base (migration 0052 à pousser) : rien à afficher.
          </p>
        </div>
      )}
      {etat.statut === 'indisponible' && (
        <div className="carte-corps">
          <p className="vide" role="status">Les agrégats d'alertes n'ont pas pu être lus (voir l'avertissement en haut de page).</p>
        </div>
      )}
      {etat.statut === 'prete' && (
        <div className="defile">
          <table>
            <thead>
              <tr>
                <th>Organisation</th><th className="c">Règles actives</th><th className="c">Zones actives</th>
                <th className="c">Alertes ouvertes</th><th className="c">Alertes 24 h</th><th className="c">Commandes 7 j</th>
                <th className="c">Erreurs d'évaluation</th>
              </tr>
            </thead>
            <tbody>
              {etat.lignes.length === 0 ? (
                <tr><td colSpan={7}><div className="vide">Aucun client n'utilise encore les alertes GPS ni un boîtier.</div></td></tr>
              ) : etat.lignes.map((l) => (
                <tr key={l.org_id}>
                  <td>
                    <div className="fort">{l.org_nom}</div>
                    <div className="discret">{LIBELLE_PALIER_BOITIER[l.palier] ?? l.palier}</div>
                  </td>
                  <td className="c n">{l.regles_actives}</td>
                  <td className="c n">{l.zones_actives}</td>
                  <td className="c n">{l.alertes_ouvertes}</td>
                  <td className="c n">{l.alertes_24h}</td>
                  <td className="c n">{l.commandes_7j}</td>
                  <td className="c n">
                    {l.erreurs_evaluation > 0
                      ? <Badge classe="rouge">{String(l.erreurs_evaluation)}</Badge>
                      : <span className="discret-t">0</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="note">
        Agrégats seulement : le contenu des alertes d'un client ne se lit pas ici. Pastille rouge = le minuteur des alertes ne tourne plus
        (voir docs/ALERTES-ET-COMMANDES-GPS.md).
      </p>
    </section>
  )
}

function Compteur({ libelle, valeur, detail, alerte = false }: { libelle: string; valeur: number; detail: string; alerte?: boolean }) {
  return (
    <div className={`carte kpi${alerte ? ' kpi-alerte' : ''}`}>
      <div className="kpi-tete"><span className="kpi-lib">{libelle}</span></div>
      <div className="kpi-v n">{valeur}</div>
      <div className="kpi-d">{detail}</div>
    </div>
  )
}

function LigneTableau({
  b, maintenant, surAttribuer, surRetirer, surDesactiver, surReactiver, surJournal, surSynchroniser, synchroEnCours, synchroBloquee,
}: {
  b: LigneBoitier
  maintenant: number
  surAttribuer: () => void
  surRetirer: () => void
  surDesactiver: () => void
  surReactiver: () => void
  surJournal: () => void
  surSynchroniser: () => void
  synchroEnCours: boolean
  synchroBloquee: boolean
}) {
  const signal = dernierSignalDe(b)
  const age = ageSignalMs(signal, maintenant)
  const nonAttribue = b.statut !== 'attribue'
  return (
    <tr>
      <td>
        <div className="fort imei">{b.imei}</div>
        {(b.nom_boitier || b.notes) && <div className="discret">{[b.nom_boitier, b.notes].filter(Boolean).join(' · ')}</div>}
      </td>
      <td><Badge classe={classeStatut(b.statut)}>{LIBELLE_STATUT[b.statut]}</Badge></td>
      <td>
        {b.org_id ? (
          <>
            <div className="fort">{b.org_nom}</div>
            <div className="discret">
              {LIBELLE_PALIER_BOITIER[b.palier ?? ''] ?? b.palier ?? '—'}
              {b.add_on_ok === false && <> · <span className="alerte-t">option indisponible : positions refusées</span></>}
              {b.attribue_le && <> · depuis le {dateHeure(b.attribue_le).split(' à ')[0]}</>}
            </div>
          </>
        ) : <span className="discret-t">—</span>}
      </td>
      <td>
        <Badge classe={classeFraicheur(age)}>{libelleAge(age)}</Badge>
        {nonAttribue && b.signaux_non_attribues > 0 && (
          <div className="discret">{b.signaux_non_attribues} message{b.signaux_non_attribues > 1 ? 's' : ''} ignoré{b.signaux_non_attribues > 1 ? 's' : ''}</div>
        )}
      </td>
      <td>
        <div className="actions">
          {b.statut === 'stock' && <button type="button" className="btn btn-plein btn-sm" onClick={surAttribuer}>Attribuer à un client</button>}
          <button
            type="button" className="btn btn-doux btn-sm" onClick={surSynchroniser} disabled={synchroBloquee} aria-busy={synchroEnCours}
            title="Crée l'appareil dans Traccar avec son IMEI et le nom donné par le client, ou le renomme"
            aria-label={`Synchroniser le boîtier ${b.imei} avec Traccar`}
          >
            <Icone nom="refresh" /> {synchroEnCours ? 'Synchronisation…' : 'Synchroniser avec Traccar'}
          </button>
          {b.org_id && <button type="button" className="btn btn-doux btn-sm" onClick={surRetirer}>Retirer</button>}
          {b.statut === 'desactive'
            ? <button type="button" className="btn btn-doux btn-sm" onClick={surReactiver}>Réactiver</button>
            : <button type="button" className="btn btn-doux btn-sm" onClick={surDesactiver}>Désactiver</button>}
          <button type="button" className="btn btn-doux btn-sm" onClick={surJournal} aria-label={`Journal du boîtier ${b.imei}`}>
            <Icone nom="history" />
          </button>
        </div>
      </td>
    </tr>
  )
}

/** Journal sous forme de liste : « Attribué à X », avec l'IMEI, l'auteur, la date et la note. */
function JournalBoitiers({ lignes, avecImei = true }: { lignes: EntreeJournalBoitier[]; avecImei?: boolean }) {
  if (lignes.length === 0) return <p className="discret-t">Aucun changement enregistré.</p>
  return (
    <ol className="journal">
      {lignes.map((e) => (
        <li key={e.id}>
          <div className="journal-tete">
            <b>{decrireJournalBoitier(e)}</b>
            <span className="discret-t">{dateHeure(e.created_at)}</span>
          </div>
          <div className="discret-t">{avecImei && <span className="imei">{e.imei} · </span>}{libelleAuteurBoitier(e)}</div>
          {e.note && <p className="journal-note">{e.note}</p>}
        </li>
      ))}
    </ol>
  )
}

function ModaleJournal({ imei, source, surFermer }: { imei: string; source: Source; surFermer: () => void }) {
  const [etat, setEtat] = useState<{ statut: 'chargement' } | { statut: 'erreur'; message: string } | { statut: 'pret'; lignes: EntreeJournalBoitier[] }>({ statut: 'chargement' })
  useEffect(() => {
    let annule = false
    source.journalBoitiers(imei).then(
      (lignes) => { if (!annule) setEtat({ statut: 'pret', lignes }) },
      (e: unknown) => { if (!annule) setEtat({ statut: 'erreur', message: messageBoitier(e) }) },
    )
    return () => { annule = true }
  }, [source, imei])
  return (
    <Modale variante="tiroir" titre="Journal du boîtier" sousTitre={imei} surFermer={surFermer}>
      {etat.statut === 'chargement' && <div className="sk" aria-busy="true" aria-label="Chargement du journal" />}
      {etat.statut === 'erreur' && <p className="encart encart-alerte" role="alert"><Icone nom="alert" /> {etat.message}</p>}
      {etat.statut === 'pret' && <JournalBoitiers lignes={etat.lignes} avecImei={false} />}
    </Modale>
  )
}

/** Champ « note » facultatif partagé par les modales. */
function ChampNote({ id, valeur, surChange, erreur, obligatoire = false }: { id: string; valeur: string; surChange: (v: string) => void; erreur: string; obligatoire?: boolean }) {
  return (
    <div className="champ">
      <label htmlFor={id}>Note {obligatoire ? <b aria-hidden="true">*</b> : <span className="discret-t">(facultative)</span>}</label>
      <textarea
        id={id} rows={2} value={valeur} maxLength={600} placeholder="Ex. Remis en main propre le 14/10/2026 à Bolt Logistics"
        onChange={(e) => surChange(e.target.value)} aria-invalid={Boolean(erreur)}
      />
      <span className="aide">Conservée dans le journal avec votre nom et la date (500 caractères au plus).</span>
      {erreur && <span className="erreur" role="alert">{erreur}</span>}
    </div>
  )
}

function ModaleAjout({ source, surFermer, surFait }: { source: Source; surFermer: () => void; surFait: (imei: string) => Promise<void> }) {
  const [imei, setImei] = useState('')
  const [note, setNote] = useState('')
  const [erreurImei, setErreurImei] = useState('')
  const [erreurNote, setErreurNote] = useState('')
  const [echec, setEchec] = useState('')
  const [enCours, setEnCours] = useState(false)

  async function valider() {
    if (enCours) return
    const i = validerImei(imei)
    const n = validerNoteBoitier(note)
    setErreurImei(i.ok ? '' : i.message)
    setErreurNote(n.ok ? '' : n.message)
    if (!i.ok || !n.ok) return
    setEnCours(true)
    setEchec('')
    try {
      await source.ajouterBoitierAuStock(i.imei, n.note)
    } catch (e) {
      setEchec(messageBoitier(e))
      setEnCours(false)
      return
    }
    await surFait(i.imei)
    setEnCours(false)
    surFermer()
  }

  return (
    <Modale
      titre="Ajouter un boîtier au stock" sousTitre="Il n'appartient à personne tant qu'il n'est pas attribué" surFermer={surFermer}
      pied={
        <>
          <button type="button" className="btn btn-doux" onClick={surFermer} disabled={enCours}>Annuler</button>
          <button type="button" className="btn btn-plein" onClick={() => void valider()} disabled={enCours}>{enCours ? 'Enregistrement…' : 'Ajouter au stock'}</button>
        </>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); void valider() }} noValidate>
        <div className="champ">
          <label htmlFor="boitier-imei">IMEI du boîtier <b aria-hidden="true">*</b></label>
          <input
            id="boitier-imei" value={imei} onChange={(e) => { setImei(e.target.value); setErreurImei('') }} maxLength={64}
            placeholder="ex. 862873087230238" autoComplete="off" inputMode="text" aria-invalid={Boolean(erreurImei)} data-focus-initial
          />
          <span className="aide">Exactement l'identifiant (uniqueId) déclaré dans Traccar, souvent l'IMEI imprimé sur le boîtier.</span>
          {erreurImei && <span className="erreur" role="alert">{erreurImei}</span>}
        </div>
        <ChampNote id="boitier-note" valeur={note} surChange={(v) => { setNote(v); setErreurNote('') }} erreur={erreurNote} />
        {echec && <p className="encart encart-alerte" role="alert"><Icone nom="alert" /> {echec}</p>}
      </form>
    </Modale>
  )
}

function ModaleAttribution({
  imei, orgs, source, surFermer, surFait,
}: {
  imei: string
  orgs: OrgPourBoitier[]
  source: Source
  surFermer: () => void
  surFait: (nomOrg: string) => Promise<void>
}) {
  const [org, setOrg] = useState('')
  const [note, setNote] = useState('')
  const [erreurOrg, setErreurOrg] = useState('')
  const [erreurNote, setErreurNote] = useState('')
  const [echec, setEchec] = useState('')
  const [enCours, setEnCours] = useState(false)
  const choisie = orgs.find((o) => o.org_id === org) ?? null

  async function valider() {
    if (enCours) return
    const n = validerNoteBoitier(note)
    setErreurOrg(choisie ? '' : 'Choisissez le client à qui attribuer ce boîtier.')
    setErreurNote(n.ok ? '' : n.message)
    if (!choisie || !n.ok) return
    setEnCours(true)
    setEchec('')
    try {
      await source.attribuerBoitier(imei, choisie.org_id, n.note)
    } catch (e) {
      setEchec(messageBoitier(e))
      setEnCours(false)
      return
    }
    await surFait(choisie.nom)
    setEnCours(false)
    surFermer()
  }

  const eligibles = orgs.filter((o) => o.eligible)
  const autres = orgs.filter((o) => !o.eligible)

  return (
    <Modale
      titre="Attribuer à un client" sousTitre={imei} surFermer={surFermer}
      pied={
        <>
          <button type="button" className="btn btn-doux" onClick={surFermer} disabled={enCours}>Annuler</button>
          <button type="button" className="btn btn-plein" onClick={() => void valider()} disabled={enCours || orgs.length === 0}>
            {enCours ? 'Attribution…' : 'Attribuer'}
          </button>
        </>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); void valider() }} noValidate>
        <p className="encart">
          <Icone nom="lock" />
          <span>
            Toutes les organisations (<b>Starter</b>, <b>Pro</b>, <b>Business</b>) peuvent recevoir un boîtier ; il est facturé à l'abonnement de suivi, par boîtier actif.
            Le client le retrouve dans sa Flotte et le rattache à un véhicule ou à un livreur (qui n'a alors pas besoin de l'application).
          </span>
        </p>
        <div className="champ">
          <label htmlFor="attribution-org">Client <b aria-hidden="true">*</b></label>
          <select id="attribution-org" value={org} onChange={(e) => { setOrg(e.target.value); setErreurOrg('') }} aria-invalid={Boolean(erreurOrg)} data-focus-initial>
            <option value="">— choisir —</option>
            {eligibles.length > 0 && (
              <optgroup label="Peuvent recevoir un boîtier">
                {eligibles.map((o) => <option key={o.org_id} value={o.org_id}>{o.nom} — {libelleEligibilite(o)}</option>)}
              </optgroup>
            )}
            {autres.length > 0 && (
              <optgroup label="Option indisponible">
                {autres.map((o) => <option key={o.org_id} value={o.org_id} disabled>{o.nom} — {libelleEligibilite(o)}</option>)}
              </optgroup>
            )}
          </select>
          {orgs.length === 0 && <span className="erreur">La liste des organisations n'a pas pu être lue : rechargez l'écran.</span>}
          {choisie && (
            <span className="aide">
              {choisie.is_operator ? 'Organisation opératrice : nos propres boîtiers.' : `Palier appliqué : ${LIBELLE_PALIER_BOITIER[choisie.palier] ?? choisie.palier}.`}{' '}
              {choisie.nb_boitiers} boîtier{choisie.nb_boitiers > 1 ? 's' : ''} déjà attribué{choisie.nb_boitiers > 1 ? 's' : ''}.
            </span>
          )}
          {erreurOrg && <span className="erreur" role="alert">{erreurOrg}</span>}
        </div>
        <ChampNote id="attribution-note" valeur={note} surChange={(v) => { setNote(v); setErreurNote('') }} erreur={erreurNote} />
        {echec && <p className="encart encart-alerte" role="alert"><Icone nom="alert" /> {echec}</p>}
      </form>
    </Modale>
  )
}

function ModaleConfirmation({
  titre, sousTitre, libelle, avertissement, danger = false, confirmer, surFermer, surFait,
}: {
  titre: string
  sousTitre: string
  libelle: string
  avertissement: string
  danger?: boolean
  confirmer: (note: string | null) => Promise<void>
  surFermer: () => void
  surFait: () => Promise<void>
}) {
  const [note, setNote] = useState('')
  const [erreur, setErreur] = useState('')
  const [echec, setEchec] = useState('')
  const [enCours, setEnCours] = useState(false)

  async function valider() {
    if (enCours) return
    const n = validerNoteBoitier(note)
    if (!n.ok) { setErreur(n.message); return }
    setEnCours(true)
    setEchec('')
    try {
      await confirmer(n.note)
    } catch (e) {
      setEchec(messageBoitier(e))
      setEnCours(false)
      return
    }
    await surFait()
    setEnCours(false)
    surFermer()
  }

  return (
    <Modale
      titre={titre} sousTitre={sousTitre} surFermer={surFermer}
      pied={
        <>
          <button type="button" className="btn btn-doux" onClick={surFermer} disabled={enCours}>Annuler</button>
          <button type="button" className={`btn ${danger ? 'btn-danger' : 'btn-plein'}`} onClick={() => void valider()} disabled={enCours}>
            {enCours ? 'En cours…' : libelle}
          </button>
        </>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); void valider() }} noValidate>
        <p className="encart encart-alerte"><Icone nom="alert" /> {avertissement}</p>
        <ChampNote id="confirmation-note" valeur={note} surChange={(v) => { setNote(v); setErreur('') }} erreur={erreur} />
        {echec && <p className="encart encart-alerte" role="alert"><Icone nom="alert" /> {echec}</p>}
      </form>
    </Modale>
  )
}
